import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../ui/select";
import { Loader2, Save, Key, Clock, ShieldAlert, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { api } from "../../../services/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";

export function PushTokenControl() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<{
    enabled: boolean;
    interval: number;
    token: string;
  } | null>(null);

  const [editEnabled, setEditEnabled] = useState(false);
  const [editInterval, setEditInterval] = useState("5");
  const [editToken, setEditToken] = useState("");
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    const result = await api.getStockbitConfig();
    if (result.success && result.data) {
      setConfig(result.data);
      setEditEnabled(result.data.enabled);
      setEditInterval(result.data.interval.toString());
      // We don't set editToken here because we only get a preview from backend
    } else {
      showToast({
        type: 'error',
        title: 'Gagal Memuat',
        message: result.error || 'Gagal memuat konfigurasi Stockbit'
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleSaveAttempt = () => {
    setShowPasswordModal(true);
  };

  const handleConfirmSave = async () => {
    if (!adminPassword) {
      showToast({
        type: 'error',
        title: 'Password Diperlukan',
        message: 'Silakan masukkan password Anda untuk melanjutkan'
      });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        enabled: editEnabled,
        interval: parseInt(editInterval),
        adminPassword
      };

      if (editToken) {
        payload.token = editToken;
      }

      const result = await api.updateStockbitConfig(payload);

      if (result.success) {
        showToast({
          type: 'success',
          title: 'Berhasil',
          message: 'Konfigurasi Stockbit berhasil diperbarui'
        });
        setShowPasswordModal(false);
        setAdminPassword("");
        setEditToken("");
        loadConfig();
      } else {
        let errorMessage = result.error || 'Terjadi kesalahan saat memperbarui konfigurasi';
        if (errorMessage === 'Invalid admin password') {
          errorMessage = 'Password yang Anda masukkan tidak sesuai';
        }
        showToast({
          type: 'error',
          title: 'Gagal Memperbarui',
          message: errorMessage
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Error',
        message: err.message || 'Terjadi kesalahan sistem'
      });
    } finally {
      setSaving(false);
    }
  };

  const isChanged = config && (
    editEnabled !== config.enabled ||
    editInterval !== config.interval.toString() ||
    editToken !== ""
  );

  if (loading && !config) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Memuat konfigurasi...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6 overflow-hidden border-t-4 border-t-amber-500 shadow-lg">
        <CardHeader className="bg-muted/30 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <ShieldAlert className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Stockbit Push Token Manager</CardTitle>
                <CardDescription>Atur interval update dan kredensial Stockbit Push Token</CardDescription>
              </div>
            </div>
            {config && (
              <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${config.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                {config.enabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {config.enabled ? 'AKTIF' : 'NONAKTIF'}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Status Toggle */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Status Otomatisasi
              </Label>
              <div className="flex items-center space-x-3 px-3 h-12 bg-muted/50 rounded-xl border border-border">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editEnabled}
                    onChange={(e) => setEditEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm font-medium">
                    {editEnabled ? 'Berjalan' : 'Berhenti'}
                  </span>
                </label>
              </div>
            </div>

            {/* Interval Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                Interval Update (Menit)
              </Label>
              <Select value={editInterval} onValueChange={setEditInterval}>
                <SelectTrigger className="w-full !h-12 bg-muted/50 rounded-xl border-border">
                  <SelectValue placeholder="Pilih interval" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Setiap 1 Menit</SelectItem>
                  <SelectItem value="2">Setiap 2 Menit</SelectItem>
                  <SelectItem value="5">Setiap 5 Menit</SelectItem>
                  <SelectItem value="10">Setiap 10 Menit</SelectItem>
                  <SelectItem value="15">Setiap 15 Menit</SelectItem>
                  <SelectItem value="30">Setiap 30 Menit</SelectItem>
                  <SelectItem value="60">Setiap 1 Jam</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Token Input */}
            <div className="space-y-3 lg:col-span-1 md:col-span-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Key className="w-4 h-4 text-primary" />
                Stockbit Token
              </Label>
              <div className="relative group">
                <textarea
                  placeholder={config?.token || "Masukkan token baru"}
                  value={editToken}
                  onChange={(e) => setEditToken(e.target.value)}
                  className="flex min-h-[48px] h-12 w-full rounded-xl border border-border bg-muted/50 px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-20 resize-y"
                  style={{ 
                    WebkitTextSecurity: showToken ? "none" : "disc",
                    // For compatibility
                    msTextSecurity: showToken ? "none" : "disc",
                  } as any}
                />
                <div className="absolute right-3 top-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <Key className="w-5 h-5 text-muted-foreground opacity-50" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground px-1">
                Kosongkan jika tidak ingin mengubah token.
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button 
              onClick={handleSaveAttempt} 
              disabled={!isChanged || saving}
              className="h-12 px-8 rounded-xl shadow-md transition-all active:scale-95"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Simpan Perubahan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Verification Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              Verifikasi Keamanan
            </DialogTitle>
            <DialogDescription>
              Masukkan password Anda untuk mengonfirmasi perubahan konfigurasi Stockbit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password Anda</Label>
              <div className="relative">
                <Input
                  id="admin-password"
                  type={showAdminPassword ? "text" : "password"}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmSave()}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  {showAdminPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
              Batal
            </Button>
            <Button onClick={handleConfirmSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Konfirmasi & Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
